/* eslint-disable @next/next/no-img-element */
import { Loading } from "@/components/overlays/Loading";
import IndicatorBadge from "@/components/status/IndicatorBadge";
import { getBaseThemeName, getTheme } from "@/features/branding";
import supabase from "@/features/supabase";
import { translucent } from "@/utils/color";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import ReloadOutlined from "@ant-design/icons/es/icons/ReloadOutlined";
import { useLocalStorage } from "@mantine/hooks";
import { useSession } from "@supabase/auth-helpers-react";
import { Alert, Button, Card, Typography } from "antd";
import Head from "next/head";
import Link from "next/link";
import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { HomeDashboardItem } from "../types";
import { getDefaultHomeDashboardItems } from "../utils";
import styles from "./HomeDashboard.module.scss";
const {
  Title
} = Typography;
export interface HomeDashboardProps {
  banner?: {
    hideOverlay?: boolean;
    image?: {
      alt: string;
      src: string;
      style?: CSSProperties;
    };
  };
  disclaimer?: ReactNode;
  customItems?: HomeDashboardItem[];
}
export const HomeDashboard = ({
  banner,
  disclaimer,
  customItems
}: HomeDashboardProps) => {
  const {
    antd
  } = getTheme();
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: "meta"
  });
  const {
    t: tTerms
  } = useTranslation("terms");
  const [hideAutomatedResilience, setHideAutomatedResilience] = useLocalStorage<boolean>({
    key: "automatedResilience.hideHomepageBanner",
    defaultValue: false
  });
  const session = useSession();
  const [items, setItems] = useState<HomeDashboardItem[]>(customItems || []);
  const [isLoading, setIsLoading] = useState(true);
  const {
    portfolioAutoAdaptabilityEnabled
  } = useAdaptationPermissions();
  useEffect(() => {
    if (customItems) {
      setItems(customItems);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    getDefaultHomeDashboardItems(session).then(setItems).finally(() => setIsLoading(false));
  }, [session, customItems]);
  const primaryColour = antd.token?.colorPrimary || "#333333";
  const cardBg = antd.components?.Menu?.itemSelectedBg || translucent(primaryColour, 0.06);
  const subtitle = items.length ? t("subtitle") : t("zeroToolsAccess.subtitle");
  const handleRefreshSession = async () => {
    await supabase.auth.refreshSession();
  };
  if (isLoading) {
    return <Loading />;
  }
  return <>
      <Head data-sentry-element="Head" data-sentry-source-file="HomeDashboard.tsx">
        <title>{t("title")}</title>
      </Head>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.imageWrapper}>
            {!banner?.hideOverlay && <div className={styles.overlay} style={{
            backgroundColor: translucent(primaryColour, 0.8)
          }} />}
            {!!banner?.image && <img alt={banner.image.alt} src={banner.image.src} style={banner.image.style} />}
          </div>
          <h1 className={styles.title}>{t("title")}</h1>
          <div className={styles.subtitleWrapper}>
            {subtitle.split("/n").map((text, index) => <p key={index} className={styles.subtitle}>
                {text}
              </p>)}
            {!items.length && <Button onClick={handleRefreshSession} icon={<ReloadOutlined />} style={{
            marginLeft: 32
          }}>
                Refresh
              </Button>}
          </div>

          {/* eslint-disable-next-line no-constant-binary-expression -- guided tour hidden for now */}
          {false && <Button className={styles.primaryButton}>Start Guided Tour</Button>}
        </div>

        {getBaseThemeName() !== "hkma" && !hideAutomatedResilience && <Alert type="warning" className="relative mt-12 rounded-lg border border-gray-200 px-8 py-6 shadow-sm" closable={{
        closeIcon: true,
        onClose: () => setHideAutomatedResilience(true)
      }} description={<>
                <div className="relative ml-10">
                  <IndicatorBadge mode="inline" placement="left" offset={[-8, -6]}>
                    <Title level={4}>{tTerms("automatedResilience")}</Title>
                  </IndicatorBadge>
                </div>

                <p className="text-md mb-3 text-yellow-800">
                  From September, eligible organisations will be able to access the NEW {tTerms("automatedResilience")}{" "}
                  capability - a decision-support tool designed to help users identify assets with the greatest adaptive
                  potential, and prioritise resilience investment across an entire portfolio.
                </p>
                <p className="mb-4 w-fit rounded-md border border-amber-200 bg-amber-100 px-4 py-2 text-sm text-yellow-800">
                  <span className="font-semibold">NOTE:</span> Contact your Customer Success Manager to have Automated
                  Resilience enabled for your organisation. During the initial release period, Automated Resilience will
                  be available for the Portfolio Analysis tool only. The capability will be extended to include the
                  Large Site Analysis Tool during later releases.
                </p>

                <div className="flex gap-2">
                  <Link href={"/method/automated-resilience"}>
                    <Button type="primary">Learn More</Button>
                  </Link>

                  {portfolioAutoAdaptabilityEnabled && <Link href={"/tools/comprehensive/portfolio-analysis"}>
                      <Button type="primary">Try it out</Button>
                    </Link>}
                </div>
              </>}></Alert>}

        {!!items.length && <>
            <Title level={4} style={{
          marginTop: 32
        }}>
              Please select a tool from the menu or explore the use cases below to get started.
            </Title>
            <div className={styles.tools}>
              {items.map(({
            title,
            icon,
            tool,
            link
          }, index) => <Card className={styles.card} key={index}>
                  <div className={styles.cardIcon} style={{
              color: "var(--color-accent)",
              backgroundColor: cardBg
            }}>
                    {icon}
                  </div>
                  <div className={styles.cardContentWrapper}>
                    <Title level={4} className={styles.cardTitle}>
                      {title}
                    </Title>
                    <Link href={link}>
                      <Button className={styles.cardButton} type="primary">
                        Start with {tool}
                      </Button>
                    </Link>
                  </div>
                </Card>)}
            </div>
          </>}

        {!!disclaimer && <>
            <p className={styles.disclaimer}>
              <b>Disclaimer</b>: {disclaimer}
            </p>
          </>}
      </div>
    </>;
};